import React from 'react';
import { Drawer, Table, Spin, Button } from 'antd';
import { ColumnType } from 'antd/es/table';
import { getSubReportConfig } from '../../data/subReportsData';

interface SubReportDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  columns: ColumnType<any>[];
  data: any[];
  loading?: boolean;
  width?: number;
  onRowClick?: (record: any, nestedSubReportId?: string) => void;
  currentSubReportId?: string;
}

const SubReportDrawer: React.FC<SubReportDrawerProps> = ({
  open,
  onClose,
  title,
  columns,
  data,
  loading = false,
  width = 1200,
  onRowClick,
  currentSubReportId,
}) => {

  // Enhance columns based on the current sub-report
  const enhancedColumns = React.useMemo(() => {
    if (!currentSubReportId || !onRowClick) return columns;

    const config = getSubReportConfig(currentSubReportId);
    if (!config?.hasNestedDrillDown) return columns;

    return columns.map(col => {
      if (config.clickableColumn && col.dataIndex === config.clickableColumn) {
        return {
          ...col,
          render: (text: any, record: any) => {
            const existingRender = col.render;
            const content = existingRender ? existingRender(text, record, 0) : text;

            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  color: '#1890ff'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRowClick(record, config.nestedReportId);
                }}
              >
                <span>{content}</span>
              </div>
            );
          },
        };
      }
      return col;
    });
  }, [columns, currentSubReportId, onRowClick]);

  const handleRowClick = (record: any) => {
    if (!currentSubReportId || !onRowClick) return;

    const config = getSubReportConfig(currentSubReportId);
    if (config?.hasNestedDrillDown && config.nestedReportId) {
      onRowClick(record, config.nestedReportId);
    }
  };

  const getRowKey = (record: any) => {
    return record.id ||
      record.workOrderLineId ||
      `${record.workOrderId}-${record.workOrderLineId}` ||
      Math.random().toString();
  };

  return (
    <Drawer
      title={title}
      width={width}
      onClose={onClose}
      open={open}
      bodyStyle={{ paddingBottom: 80 }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={enhancedColumns}
          dataSource={data}
          scroll={{ x: 'max-content' }}
          rowKey={getRowKey}
          onRow={(record) => {
            const config = getSubReportConfig(currentSubReportId || '');
            const isClickable = config?.hasNestedDrillDown && config.nestedReportId;

            const rowProps: any = {
              style: {
                cursor: isClickable ? 'pointer' : 'default',
              },
            };

            if (isClickable) {
              rowProps.onClick = () => handleRowClick(record);
              rowProps.style = {
                ...rowProps.style,
                '&:hover': { backgroundColor: '#f5f5f5' }
              };
            }

            return rowProps;
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: false,
          }}
        />
      )}
    </Drawer>
  );
};

export default SubReportDrawer;